/**
 * `@core/num` — the big-number layer (tech-design §2.6).
 *
 * This is the **only** module in the codebase allowed to import
 * `break_infinity.js`; everything else speaks {@link Num}. Wrapping it behind our
 * own surface means we can swap to break_eternity later (layered exponents) without
 * touching the engine, and it gives us one place for Decimal-safe (de)serialization
 * and human formatting (`1.23 M` → `4.56 Qa` → scientific).
 *
 * `Num` values are immutable — every operation returns a new value, which is what
 * lets the engine treat state as persistent data.
 */
import Decimal from 'break_infinity.js';

/** A big number. Opaque to the rest of the engine — operate on it via this module. */
export type Num = Decimal;

/** Anything that can be coerced into a {@link Num}. */
export type NumSource = number | string | Num;

export const ZERO: Num = new Decimal(0);
export const ONE: Num = new Decimal(1);

/** Construct a {@link Num} from a number, a Decimal-safe string, or another Num. */
export function num(value: NumSource): Num {
  return new Decimal(value);
}

// ── Arithmetic (all pure) ─────────────────────────────────────────────────────

export const add = (a: NumSource, b: NumSource): Num => new Decimal(a).add(b);
export const sub = (a: NumSource, b: NumSource): Num => new Decimal(a).sub(b);
export const mul = (a: NumSource, b: NumSource): Num => new Decimal(a).mul(b);
export const div = (a: NumSource, b: NumSource): Num => new Decimal(a).div(b);
export const pow = (a: NumSource, exponent: number): Num => new Decimal(a).pow(exponent);
export const neg = (a: NumSource): Num => new Decimal(a).mul(-1);
export const floor = (a: NumSource): Num => new Decimal(a).floor();
export const max = (a: NumSource, b: NumSource): Num => Decimal.max(a, b);
export const min = (a: NumSource, b: NumSource): Num => Decimal.min(a, b);

// ── Comparison ────────────────────────────────────────────────────────────────

/** -1, 0, or 1. */
export const cmp = (a: NumSource, b: NumSource): number => new Decimal(a).cmp(b);
export const gt = (a: NumSource, b: NumSource): boolean => new Decimal(a).gt(b);
export const gte = (a: NumSource, b: NumSource): boolean => new Decimal(a).gte(b);
export const lt = (a: NumSource, b: NumSource): boolean => new Decimal(a).lt(b);
export const lte = (a: NumSource, b: NumSource): boolean => new Decimal(a).lte(b);
export const eq = (a: NumSource, b: NumSource): boolean => new Decimal(a).eq(b);
export const isZero = (a: NumSource): boolean => new Decimal(a).eq(ZERO);
export const isNegative = (a: NumSource): boolean => new Decimal(a).lt(ZERO);
export const toNumber = (a: NumSource): number => new Decimal(a).toNumber();

// ── Serialization (Decimal-safe through JSON) ─────────────────────────────────

/** Encode a {@link Num} as a string that round-trips exactly through JSON (§2.6). */
export const serialize = (a: Num): string => a.toString();

/** Decode a {@link Num} previously produced by {@link serialize}. */
export const parse = (s: string): Num => new Decimal(s);

// ── Formatting ────────────────────────────────────────────────────────────────

/**
 * Short-scale suffixes for the readable band. `''` covers < 1000; the rest step
 * every 1e3. Beyond the table we fall back to scientific notation.
 */
const SUFFIXES = [
  '',
  'K',
  'M',
  'B',
  'T',
  'Qa',
  'Qi',
  'Sx',
  'Sp',
  'Oc',
  'No',
  'Dc',
] as const;

/** Player-selectable number notation (settings, GAM-25). */
export type Notation = 'letters' | 'scientific';

/**
 * Human-readable formatting: `1234` → `1.23 K`, `4.5e18` → `4.50 Qi`, and anything
 * past the suffix table → scientific (`1.23e40`). Small values render with a few
 * decimals; integers below 1000 render bare, unless `alwaysDecimals` is set (a
 * list of several such numbers side by side reads better all fixed to the same
 * width than a mix of bare and decimal). Passing `notation: 'scientific'` skips
 * the suffix table so everything at 1000+ renders as `1.23e6`.
 */
export function format(
  value: NumSource,
  decimals = 2,
  notation: Notation = 'letters',
  alwaysDecimals = false,
): string {
  const n = new Decimal(value);
  if (n.eq(ZERO)) return alwaysDecimals ? (0).toFixed(decimals) : '0';

  const sign = n.lt(ZERO) ? '-' : '';
  const abs = n.abs();

  // Below 1000: bare integer, or a few decimals for fractional values.
  if (abs.lt(1000)) {
    const asNumber = abs.toNumber();
    const text =
      !alwaysDecimals && Number.isInteger(asNumber)
        ? String(asNumber)
        : asNumber.toFixed(decimals);
    return `${sign}${text}`;
  }

  const exponent = abs.exponent;
  const group = Math.floor(exponent / 3);
  const suffix = SUFFIXES[group];
  if (notation === 'letters' && suffix !== undefined) {
    // mantissa in [1, 1000) for this group.
    const mantissa = abs.div(Decimal.fromMantissaExponent(1, group * 3)).toNumber();
    return `${sign}${mantissa.toFixed(decimals)} ${suffix}`;
  }

  // Past the named scale: scientific, e.g. "1.23e40".
  return `${sign}${abs.mantissa.toFixed(decimals)}e${String(abs.exponent)}`;
}
